import { IconButton } from "@educaplay/core/components";
import { Button, CONTENT_TYPE, NAME_SOUND_ACTIVE, NAME_SOUND_HOVER, TYPE_QUESTIONS, useGameSounds, useMediaQuery, useTranslate } from '@educaplay/core';
import classes from './MultiQuestion.module.scss'
import { Search } from '@educaplay/core/Icons/Search';
import { Dialog } from "@educaplay/core/components";
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LetterContainer from '../../LetterContainer';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';

import { Check } from '@educaplay/core/Icons/Check';
import MsgContainer from '../../MsgContainer';
import { TEST_SOUNDS } from '../../../utils/constants';
import { NextSubmitButton } from "@educaplay/core/components";
import { AudioComponentControl } from "@educaplay/core/components/AudioComponentControl";
import { setAnimating } from "@educaplay/store/slices/gameSlice";
import { SCREENS } from "@educaplay/store/constants";

const ANSWER_DELAY = 1000;
export const MultiQuestion = (props) => {
    const { question, onAnswer } = props;
    const [dialog, setDialog] = useState({
        open: false,
        source: null
    });

    const [selectedOptions, setSelectedOptions] = useState(() => question.answer?.answer ?? []);
    const [answersFeedback, setAnswersFeedback] = useState(null);
    const questionsContainer = useRef();
    const t = useTranslate();
    const gameSound = useGameSounds();
    const isTouchDevice = (useMediaQuery("(hover:none)") || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0));
    const isExamMode = useSelector(state => state.game.examMode);
    const isAnimating = useSelector(state => state.game.isAnimating);
    const finishEntryAnimation = useSelector(state => state.game.isCompleteEntryAnimation);
    const [hasAnswered, setHasAnswered] = useState(false);
    const liveActived = useSelector(state => state.game.liveActived);
    const currentIndex = useSelector(state => state.game.currentIndex);
    const screen = useSelector(state => state.game.view);
    const dispatch = useDispatch();

    const answers = question.answers.map((answer) => {
        return {
            ...answer,
            className: answersFeedback?.[answer.id] ?? ''
        };
    });

    const handleChangeSelection = (ev) => {
        if (isAnimating || !finishEntryAnimation || hasAnswered) return;
        if (dialog.open) return;

        const { name: id, checked } = ev.target;

        let newSelectedOptions;
        if (checked) {
            gameSound.play(NAME_SOUND_ACTIVE);
            newSelectedOptions = [...selectedOptions, id];
        } else {
            gameSound.play(TEST_SOUNDS.DESELECT);
            newSelectedOptions = selectedOptions.filter(option => option !== id);
        }

        setSelectedOptions(newSelectedOptions);

        const userAnswer = {
            type: TYPE_QUESTIONS.Selection,
            answer: newSelectedOptions
        }

        if (isExamMode) onAnswer(userAnswer);

    }

    const isAnySelected = selectedOptions.length > 0;

    useEffect(() => {
        if (isAnySelected && !isExamMode) gameSound.play(TEST_SOUNDS.SHOW_BUTTON);
    }, [isAnySelected, gameSound, isExamMode]);

    const onSubmit = (e) => {
        e.preventDefault();
        if (isAnimating || !finishEntryAnimation || hasAnswered || screen === SCREENS.SCORE_SCREEN) return;
        dispatch(setAnimating(true));
        setHasAnswered(true);
        gameSound.play(TEST_SOUNDS.SUBMIT);

        const userAnswer = {
            type: TYPE_QUESTIONS.Selection,
            answer: selectedOptions
        }

        const answerStyles = answers.reduce((acc, answer) => {
            const isUserAnswer = userAnswer.answer.includes(answer.id);
            const userAnswerStyleValue = question.correctAnswers.includes(answer.id) ? classes.correct : classes.wrong
            return {
                ...acc,
                [answer.id]: isUserAnswer ? userAnswerStyleValue : '',
            }
        }, {});
        setTimeout(() => {
            setAnswersFeedback(answerStyles);
        }, ANSWER_DELAY + 400);

        setTimeout(() => onAnswer(
            userAnswer,
            {
                showFeedback: !(question.checkAnswer(userAnswer) && !liveActived),
                questionIndexAnswer: currentIndex
            }
        ), ANSWER_DELAY);
    }

    const handleHover = () => {
        gameSound.play(NAME_SOUND_HOVER);
    }

    useEffect(() => {
        questionsContainer.current.style.setProperty('--row-questions', question.answers.length);
        questionsContainer.current.style.setProperty('--desktop-row-questions', Math.ceil(question.answers.length / 2));
    }, [question])

    const variants = {
        initial: {
            // y: "-50%",
            opacity: 0
        },
        enter: {
            opacity: 1,
            transition: {
                delayChildren: 0.8,
                staggerChildren: 0.1,
            },
        },
        exit: {
            // y: "0%",
            opacity: 0,
            transition: {
                type: 'tween',
                duration: 1.5
            }
        }
    };



    return (
        <>
            <Dialog open={dialog.open}
                onClose={() => {
                    setDialog({ open: false, source: null });
                }}
                variant="lightbox">
                <img src={dialog.source} alt="" />
            </Dialog>
            <AnimatePresence>
                <motion.form
                    className={classes.formContainer}
                    onSubmit={onSubmit}
                    key={`question_${question.id}`}
                    variants={variants}
                    initial="initial"
                    animate="enter"
                    exit={{ opacity: 0 }}
                >
                    <MsgContainer />
                    <div className={classes.questionsContainer} ref={questionsContainer}>

                        {answers.map((answer, index) => (
                            <motion.div key={`answer_${index}`} className={clsx(classes.individualQuestion, answer.className)} variants={variants} onMouseEnter={handleHover}>
                                <label className={classes.clickableZone}>
                                    <input
                                        type="checkbox"
                                        name={answer.id}
                                        onChange={handleChangeSelection}
                                        className={classes.invisibleInput}
                                        checked={selectedOptions.includes(answer.id)}
                                    />

                                    <LetterContainer index={index} />
                                    <div className={clsx(classes.contentContainer, selectedOptions.includes(answer.id) && classes[`selected`], answer.className)}>
                                        {answer.type && (
                                            <div className={classes.multimediaContainer}>

                                                {answer.type === CONTENT_TYPE.IMAGE && (
                                                    < div className={clsx(classes.sourceMedia, classes.borderimg)}>
                                                        <img src={answer.source} onClick={() => { setDialog({ open: true, source: answer.source }) }} />
                                                        <IconButton
                                                            icon={<Search className={classes.paddingIcon} />}
                                                            className={classes.expandIcon}
                                                            onClick={() => { setDialog({ open: true, source: answer.source }) }} />
                                                    </div>
                                                )}

                                                {answer.type === CONTENT_TYPE.AUDIO && (
                                                    <div className={classes.sourceMedia}>
                                                        <AudioComponentControl source={answer.source} className={classes.audioComponent} />
                                                    </div>
                                                )}

                                            </div>

                                        )}
                                        {answer.answer && (
                                            <div className={clsx(classes.textContainer, classes.scrollbar)}>
                                                {answer.answer}
                                            </div>)}
                                    </div>

                                </label>
                            </motion.div>
                        ))}

                    </div >

                    {isAnySelected && !isExamMode && isTouchDevice && (
                        <div className={classes.buttonContainer}>

                            <NextSubmitButton showSubmit handleHover={handleHover} />
                        </div>)}

                    {isAnySelected && !isExamMode && !isTouchDevice && (
                        <div className={classes.buttonContainer}>
                            <Button type={"submit"}
                                icon={<Check />}
                                className={classes.sendButtonDesktop}
                                onMouseEnter={handleHover}>
                                {t('common.settings.accept')}
                            </Button>

                        </div>)}
                </motion.form>
            </AnimatePresence>
        </>
    );
}